import React, { useEffect } from "react";
import { DEBT_LIST_STATUS_FILTER_OPTIONS } from "../DebtCollectionUIHelpers";

/**
 * Presentational filter controls for the debt collection list (debounced search, selects, exports).
 * @param {object} props - Form field props from Formik and callbacks from parent.
 * @param {import("react-intl").IntlShape} props.intl - react-intl instance for labels.
 * @param {function} props.handleBlur - Formik blur handler.
 * @param {object} props.values - Formik values (`searchText`, `franchiseId`, `status`).
 * @param {function} props.setFieldValue - Formik field setter.
 * @param {function} props.handleSubmit - Formik submit handler.
 * @param {function} props.applyFilter - Applies current values to list query state.
 * @param {object[]} props.franchiseOptions - Franchise rows for the dropdown.
 * @param {boolean} [props.showFranchiseFilter=true] - Whether franchise select is visible.
 * @param {string|null} [props.exportBusy=null] - Active export key (`summary`|`ledger`) or null.
 * @param {function} [props.onExportSummary] - Starts summary CSV export.
 * @param {function} [props.onExportLedger] - Starts ledger CSV export.
 * @returns {JSX.Element} Filter row markup.
 */
export function DebtCollectionFilterForm({
  intl,
  handleBlur,
  values,
  setFieldValue,
  handleSubmit,
  applyFilter,
  franchiseOptions,
  showFranchiseFilter = true,
  exportBusy = null,
  onExportSummary,
  onExportLedger,
}) {
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSubmit();
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.searchText]);

  const onStatusChange = (e) => {
    const v = e.target.value;
    setFieldValue("status", v);
    applyFilter({
      ...values,
      status: v,
    });
  };

  const onFranchiseChange = (e) => {
    const v = e.target.value;
    setFieldValue("franchiseId", v);
    applyFilter({
      ...values,
      franchiseId: v,
    });
  };

  return (
    <div className="form-group row align-items-end mx-0 w-100 flex-wrap">
      <div className="col d-flex flex-wrap align-items-end pl-0 pr-md-2">
        <div className="col-6 col-sm-4 col-lg-2 pr-2 mb-2 mb-lg-0 pl-0">
          <label
            className="d-block small text-muted mb-1"
            style={{ fontSize: "0.75rem" }}
          >
            Status
          </label>
          <select
            className="form-control form-control-sm"
            name="status"
            value={values.status}
            onChange={onStatusChange}
            onBlur={handleBlur}
          >
            {DEBT_LIST_STATUS_FILTER_OPTIONS.map((opt) => (
              <option
                key={opt.value === "" ? "all" : opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {showFranchiseFilter ? (
          <div className="col-6 col-sm-4 col-lg-2 pr-2 mb-2 mb-lg-0 pl-0">
            <label
              className="d-block small text-muted mb-1"
              style={{ fontSize: "0.75rem" }}
            >
              Franchise
            </label>
            <select
              className="form-control form-control-sm"
              name="franchiseId"
              value={values.franchiseId}
              onChange={onFranchiseChange}
              onBlur={handleBlur}
            >
              <option value="">All franchises</option>
              {franchiseOptions.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.name || f.short_name || `Franchise ${f.id}`}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      <div className="col-12 col-sm-auto ml-sm-auto mt-2 mt-lg-0 mb-2 mb-lg-0 pl-0 pr-0">
        <div className="d-flex flex-column flex-sm-row flex-sm-nowrap align-items-stretch align-items-sm-center justify-content-sm-end">
          {onExportSummary && onExportLedger ? (
            <div className="d-flex flex-wrap align-items-center flex-shrink-0 mb-2 mb-sm-0 mr-sm-3">
              <button
                type="button"
                className="btn btn-sm btn-light font-weight-bold text-dark-75 mr-2"
                disabled={exportBusy != null}
                onClick={onExportSummary}
              >
                {exportBusy === "summary" ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : null}
                Summary report
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light font-weight-bold text-dark-75"
                disabled={exportBusy != null}
                onClick={onExportLedger}
              >
                {exportBusy === "ledger" ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : null}
                Detailed report
              </button>
            </div>
          ) : null}
          <div
            className="position-relative w-100 flex-grow-1 flex-sm-grow-0"
            style={{ maxWidth: "220px", minWidth: "140px" }}
          >
            <input
              type="text"
              className="form-control"
              name="searchText"
              placeholder={intl.formatMessage({ id: "Search" })}
              style={{ paddingRight: "40px" }}
              onBlur={handleBlur}
              value={values.searchText}
              onChange={(e) => {
                setFieldValue("searchText", e.target.value);
              }}
              autoComplete="off"
            />
            <i
              className="fa fa-search"
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: "15px",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
